import {
  AreaImageBusiness,
  AreaImageSlug,
  BoxBusiness,
  BoxLogin,
  ButtonSubmitLogin,
  Container,
  FormLogin,
  CustomForm,
  InputContainer,
  InputLabel,
  CustomInput,
  ErrorMessage,
} from "./styles";

import { useCallback, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { useLoading } from "../../../hooks/loading";
import useNotification from "../../../hooks/newUXNotification";
import { useParams } from "react-router-dom";
import { AnimatedContainer } from "../styles";
import ProportionalImage from "../../../components/ProportionalImage";
import VirtualKeyboard from "../../../components/VirtualKeyboard";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { useAccessCodeValidationSchema } from "./validationSchema";
import { LOGO_URLS } from "./constants";
import { changeApplicationLanguage } from "../../../redux/modules/language/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  getSlug,
  verifyCodeAccessAndPosition,
  totemOverrideLogin,
} from "../../../service/selfservice.service";
import {
  saveTotemPosition,
  getTotemPosition,
} from "../../../utils/totemPosition";
import OverrideModal from "../../../newUXPages/SelfService/components/OverrideModal";

const SignInSelfService = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { code } = useSelector((state) => state.language);
  const history = useHistory();
  const { handleLoading } = useLoading();
  const notification = useNotification();
  const [logoUrl, setLogoUrl] = useState();
  const [logoInformaMarkets] = useState(LOGO_URLS.INFORMA_MARKETS);
  const [errors, setErrors] = useState({});
  const [accessCode, setAccessCode] = useState("");
  const [totemPosition, setTotemPosition] = useState("");
  const [showTotemField, setShowTotemField] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [activeField, setActiveField] = useState("accessCode");
  const validationSchema = useAccessCodeValidationSchema(intl);
  const slug = params.slug;
  const submitButtonRef = useRef(null);
  const [errorGetSlug, setErrorGetSlug] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [savedPosition, setSavedPosition] = useState(null);
  const [allowedRange, setAllowedRange] = useState(null);

  const storeSessionDataInLocalStorage = useCallback(
    (accessCode, token = null, printConfig = null) => {
      try {
        localStorage.setItem("accessCode", accessCode);
        localStorage.setItem("slugName", slug);
        if (token) {
          localStorage.setItem("token", token);
        }
        if (printConfig) {
          localStorage.setItem("printConfiguration", printConfig);
        }
      } catch (error) {
        console.warn(
          "Erro ao armazenar dados da sessão no localStorage:",
          error
        );
      }
    },
    [slug]
  );

  const handleInputChange = useCallback(
    (fieldName, value) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: undefined,
      }));

      if (fieldName === "accessCode") {
        storeSessionDataInLocalStorage(value);
        setAccessCode(value);
      } else if (fieldName === "totemPosition") {
        setTotemPosition(value);
        // Limpar mensagem de range inválido ao digitar
        setAllowedRange(null);
      }
    },
    [storeSessionDataInLocalStorage]
  );

  const handleKeyPress = useCallback(
    (key) => {
      if (activeField === "accessCode") {
        const newValue = accessCode + key;
        setAccessCode(newValue);
        handleInputChange("accessCode", newValue);
      } else if (activeField === "totemPosition") {
        const newValue = totemPosition + key;
        setTotemPosition(newValue);
        handleInputChange("totemPosition", newValue);
      }
    },
    [accessCode, totemPosition, activeField, handleInputChange]
  );

  const handleBackspace = useCallback(() => {
    if (activeField === "accessCode") {
      const newValue = accessCode.slice(0, -1);
      setAccessCode(newValue);
      handleInputChange("accessCode", newValue);
    } else if (activeField === "totemPosition") {
      const newValue = totemPosition.slice(0, -1);
      setTotemPosition(newValue);
      handleInputChange("totemPosition", newValue);
    }
  }, [accessCode, totemPosition, activeField, handleInputChange]);

  const handleClear = useCallback(() => {
    if (activeField === "accessCode") {
      setAccessCode("");
      handleInputChange("accessCode", "");
    } else if (activeField === "totemPosition") {
      setTotemPosition("");
      handleInputChange("totemPosition", "");
    }
  }, [activeField, handleInputChange]);

  const handleCloseKeyboard = useCallback(() => {
    setIsKeyboardVisible(false);
  }, []);

  const handleEnterKey = useCallback(() => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  }, []);

  const handleInputFocus = useCallback((fieldName) => {
    setActiveField(fieldName);
    setIsKeyboardVisible(true);
  }, []);

  const resetForm = () => {
    setShowTotemField(false);
    setActiveField("accessCode");
    setIsKeyboardVisible(false);
    setAllowedRange(null);
  };

  /**
   * Função reutilizável para tratar códigos de erro da API
   * @param {number} errorCode - Código de erro retornado pela API
   * @param {object} errorData - Dados adicionais do erro
   * @param {object} errorData.allowedRange - Intervalo permitido para posições (quando aplicável)
   * @param {number} errorData.allowedRange.startPosition - Posição inicial do intervalo
   * @param {number} errorData.allowedRange.endPosition - Posição final do intervalo
   * @param {object} loginData - Dados de login para fallback
   * @param {string} loginData.code - Código de acesso
   * @param {number} loginData.position - Posição do totem
   */
  const handleErrorCode = useCallback(
    (errorCode, errorData = {}, loginData = {}) => {
      switch (errorCode) {
        case -50015: {
          // Posição fora do intervalo configurado
          const range = errorData.allowedRange;
          if (range) {
            setAllowedRange(range);
            notification.displayError(
              intl.formatMessage(
                { id: "totem.position.out_of_range" },
                { start: range.startPosition, end: range.endPosition }
              )
            );
          } else {
            notification.displayError(
              intl.formatMessage({ id: "totem.error.position_not_in_range" })
            );
          }
          break;
        }

        case -50004:
          resetForm();
          setAllowedRange(null);
          notification.displayError(
            intl.formatMessage({ id: "totem.error.invalid_access_code" })
          );
          break;

        case -50005:
          // Posição em uso - abrir modal de override
          setPendingLogin({
            slug: slug,
            code: loginData.code || accessCode,
            position: loginData.position || parseInt(totemPosition, 10),
          });
          setShowOverrideModal(true);
          break;

        case -50009:
          notification.displayError(
            intl.formatMessage({ id: "totem.error.no_templates" })
          );
          break;

        case -50016:
          notification.displayError(
            intl.formatMessage({ id: "totem.error.area_without_range" })
          );
          break;

        default:
          notification.displayError(
            intl.formatMessage({ id: "totem.error.request_failed" })
          );
          break;
      }
    },
    [intl, notification, slug, accessCode, totemPosition]
  );

  const enterFullscreen = async () => {
    try {
      if (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      ) {
        return;
      }

      if (
        !document.documentElement.requestFullscreen &&
        !document.documentElement.webkitRequestFullscreen &&
        !document.documentElement.msRequestFullscreen
      ) {
        console.warn("API de fullscreen não está disponível neste navegador");
        return;
      }

      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        await document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        await document.documentElement.msRequestFullscreen();
      }
    } catch (err) {
      console.warn("Erro ao tentar entrar em fullscreen:", err);

      if (err.name === "NotAllowedError") {
        console.warn(
          "Fullscreen bloqueado pela política de segurança do navegador. É necessária interação do usuário."
        );
      }
    }
  };

  const handleSubmit = useCallback(
    async (data) => {
      try {
        setErrors({});

        // verifica se existe valor para codigo de acesso
        if (!showTotemField) {
          // Carregar posição salva do localStorage se existir
          if (savedPosition) {
            setTotemPosition(savedPosition.toString());
            notification.displayInfo(
              intl.formatMessage(
                { id: "totem.position.using_saved" },
                { position: savedPosition }
              )
            );
          }
          
          setShowTotemField(true);
          setActiveField("totemPosition");
          setIsKeyboardVisible(false);

          return;
        }

        // Segunda etapa: validar ambos os campos e fazer submit final
        const dataToValidate = { accessCode, totemPosition };
        const validatedData = validationSchema.parse(dataToValidate);

        handleLoading(true);

        // get code access
        const responseCodeAccess = await verifyCodeAccessAndPosition({
          slug: slug,
          code: validatedData.accessCode,
          position: validatedData.totemPosition,
        });

        if (responseCodeAccess.status === 200) {
          switch (true) {
            case responseCodeAccess.data.code === 50008: {
              const token = responseCodeAccess.data.token;
              // Fallback para STAMP se printConfiguration não vier do backend
              const printConfig = responseCodeAccess.data.printConfiguration || "STAMP";
              setIsKeyboardVisible(false);
              
              // Salvar posição no localStorage
              saveTotemPosition(slug, parseInt(validatedData.totemPosition, 10));
              notification.displaySuccess(
                intl.formatMessage(
                  { id: "totem.position.saved" },
                  { position: validatedData.totemPosition }
                )
              );
              
              storeSessionDataInLocalStorage(
                validatedData.accessCode,
                token,
                printConfig
              );

              await enterFullscreen();
              history.push(`/totem/${slug}/details`);
              break;
            }

            case responseCodeAccess.data.code === -50015:
            case responseCodeAccess.data.code === -50004:
            case responseCodeAccess.data.code === -50005:
            case responseCodeAccess.data.code === -50009:
            case responseCodeAccess.data.code === -50016:
              handleErrorCode(
                responseCodeAccess.data.code,
                responseCodeAccess.data,
                {
                  code: validatedData.accessCode,
                  position: parseInt(validatedData.totemPosition, 10),
                }
              );
              break;

            default:
              notification.displayError(
                intl.formatMessage({ id: "totem.error.request_failed" })
              );
              break;
          }
        }
      } catch (err) {
        if (err.name === "ZodError") {
          const formattedErrors = {};
          err.errors.forEach((error) => {
            formattedErrors[error.path[0]] = error.message;
          });
          setErrors(formattedErrors);
        } else if (err.response?.data) {
          // Tratamento de erros da API
          handleErrorCode(
            err.response.data.code,
            err.response.data,
            {
              code: accessCode,
              position: parseInt(totemPosition, 10),
            }
          );
        } else {
          console.error("Submission error:", err);
          notification.displayError(
            intl.formatMessage({ id: "totem.error.request_failed" })
          );
        }
      } finally {
        handleLoading(false);
      }
    },
    [
      handleLoading,
      validationSchema,
      history,
      notification,
      intl,
      accessCode,
      totemPosition,
      showTotemField,
      savedPosition,
      slug,
      storeSessionDataInLocalStorage,
      handleErrorCode,
    ]
  );

  const getLogoToSlug = useCallback(async () => {
    handleLoading(true);
    try {
      const responseSlug = await getSlug(slug);

      if (responseSlug.status === 200 && responseSlug.data.code === 50010) {
        // Se eventLogoUrl for null ou undefined, não setar (evita loop)
        if (responseSlug.data.eventLogoUrl) {
          setLogoUrl(responseSlug.data.eventLogoUrl);
        }
        setErrorGetSlug(false);
      }
    } catch (error) {
      console.error('[Totem] Erro ao buscar slug:', error);
      setErrorGetSlug(true);
      notification.displayError(
        intl.formatMessage({ id: "totem.error.slug_not_found" })
      );
    } finally {
      handleLoading(false);
    }
  }, [slug, handleLoading, intl, notification]);

  /**
   * Handler para confirmar override de posição em uso
   * @param {string} reason - Motivo fornecido pelo usuário
   */
  const handleOverrideConfirm = useCallback(
    async (reason) => {
      if (!pendingLogin) return;

      try {
        handleLoading(true);
        
        const response = await totemOverrideLogin(
          pendingLogin.slug,
          pendingLogin.code,
          pendingLogin.position,
          reason
        );

        if (response.status === 200 && response.data.code === 50008) {
          const token = response.data.token;
          const printConfig = response.data.printConfiguration || "STAMP";
          
          // Salvar posição no localStorage
          saveTotemPosition(slug, pendingLogin.position);
          notification.displaySuccess(
            intl.formatMessage(
              { id: "totem.position.saved" },
              { position: pendingLogin.position }
            )
          );
          
          storeSessionDataInLocalStorage(
            pendingLogin.code,
            token,
            printConfig
          );

          // Fechar modal e resetar estado
          setShowOverrideModal(false);
          setPendingLogin(null);
          setIsKeyboardVisible(false);

          await enterFullscreen();
          history.push(`/totem/${slug}/details`);
        } else {
          notification.displayError(
            intl.formatMessage({ id: "totem.error.override_failed" })
          );
        }
      } catch (error) {
        console.error("Override error:", error);
        notification.displayError(
          intl.formatMessage({ id: "totem.error.override_connection" })
        );
      } finally {
        handleLoading(false);
      }
    },
    [
      pendingLogin,
      slug,
      handleLoading,
      intl,
      notification,
      storeSessionDataInLocalStorage,
      history,
    ]
  );

  /**
   * Handler para cancelar override
   */
  const handleOverrideCancel = useCallback(() => {
    setShowOverrideModal(false);
    setPendingLogin(null);
    resetForm();
  }, []);

  useEffect(() => {
    if (code === "en") {
      dispatch(
        changeApplicationLanguage({
          code: "pt",
        })
      );
    }

    localStorage.removeItem("accessCode");
    // localStorage.removeItem("totemName");
    localStorage.removeItem("slugName");
    localStorage.removeItem("token");
    localStorage.removeItem("printConfiguration");

    // Carregar posição salva do localStorage para este slug
    const saved = getTotemPosition(slug);
    if (saved) {
      setSavedPosition(saved);
    }

    getLogoToSlug();

    // Desabilita o clique com o botão direito
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);

    // Limpeza ao desmontar
    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, code, dispatch]);

  // Desabilita o teclado físico quando o virtual estiver ativo
  useEffect(() => {
    if (!isKeyboardVisible) return;

    const handleKeydown = (e) => {
      // Desabilita todas as teclas do teclado físico quando o virtual estiver ativo
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const handleKeyup = (e) => {
      // Desabilita todas as teclas do teclado físico quando o virtual estiver ativo
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const handleKeypress = (e) => {
      // Desabilita todas as teclas do teclado físico quando o virtual estiver ativo
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Adiciona os event listeners com capture para interceptar antes de outros handlers
    document.addEventListener("keydown", handleKeydown, { capture: true });
    document.addEventListener("keyup", handleKeyup, { capture: true });
    document.addEventListener("keypress", handleKeypress, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeydown, { capture: true });
      document.removeEventListener("keyup", handleKeyup, { capture: true });
      document.removeEventListener("keypress", handleKeypress, {
        capture: true,
      });
    };
  }, [isKeyboardVisible]);

  return (
    <LoadingOverlay>
      <Container>
        <AnimatedContainer>
          <BoxLogin>
            <AreaImageSlug>
              {logoUrl && (
                <ProportionalImage
                  src={logoUrl}
                  alt="Logo to slug"
                  width="320px"
                  height="auto"
                />
              )}
            </AreaImageSlug>

            <FormLogin>
              <CustomForm
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                noValidate
                autoComplete="off"
              >
                {!showTotemField && (
                  <InputContainer>
                    <InputLabel>
                      {intl.formatMessage({ id: "totem.login.access_code" })}
                    </InputLabel>
                    <CustomInput
                      name="accessCode"
                      type="text"
                      placeholder={intl.formatMessage({ id: "totem.login.access_code_placeholder" })}
                      autoComplete="off"
                      value={accessCode}
                      readOnly
                      onFocus={() => handleInputFocus("accessCode")}
                      onClick={() => handleInputFocus("accessCode")}
                      onTouchStart={() => handleInputFocus("accessCode")}
                      $hasError={!!errors.accessCode}
                    />
                    {errors.accessCode && (
                      <ErrorMessage>{errors.accessCode}</ErrorMessage>
                    )}
                  </InputContainer>
                )}

                {showTotemField && (
                  <InputContainer>
                    <InputLabel>
                      {intl.formatMessage({ id: "totem.login.position_label" })}
                    </InputLabel>
                    <CustomInput
                      name="totemPosition"
                      type="text"
                      placeholder={intl.formatMessage({ id: "totem.login.position_placeholder" })}
                      autoComplete="off"
                      value={totemPosition}
                      readOnly
                      onFocus={() => handleInputFocus("totemPosition")}
                      onClick={() => handleInputFocus("totemPosition")}
                      onTouchStart={() => handleInputFocus("totemPosition")}
                      $hasError={!!errors.totemPosition || !!allowedRange}
                    />
                    {errors.totemPosition && (
                      <ErrorMessage>{errors.totemPosition}</ErrorMessage>
                    )}
                    {allowedRange && (
                      <ErrorMessage style={{ marginTop: "4px", fontSize: "13px" }}>
                        {intl.formatMessage(
                          { id: "totem.position.allowed_range_hint" },
                          { start: allowedRange.startPosition, end: allowedRange.endPosition }
                        )}
                      </ErrorMessage>
                    )}
                  </InputContainer>
                )}

                <ButtonSubmitLogin
                  ref={submitButtonRef}
                  type="submit"
                  disabled={
                    !accessCode ||
                    errorGetSlug ||
                    (showTotemField && !totemPosition)
                  }
                >
                  {showTotemField 
                    ? intl.formatMessage({ id: "totem.login.send" })
                    : intl.formatMessage({ id: "totem.login.submit" })
                  }
                </ButtonSubmitLogin>
              </CustomForm>
            </FormLogin>
          </BoxLogin>

          <BoxBusiness>
            <AreaImageBusiness>
              {logoInformaMarkets && (
                <ProportionalImage
                  src={logoInformaMarkets}
                  alt="Logo Informa Markets"
                  width="320px"
                  height="auto"
                />
              )}
            </AreaImageBusiness>
          </BoxBusiness>
        </AnimatedContainer>

        <VirtualKeyboard
          isVisible={isKeyboardVisible}
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClear={handleClear}
          onClose={handleCloseKeyboard}
          onEnter={handleEnterKey}
          value={activeField === "accessCode" ? accessCode : totemPosition}
        />

        <OverrideModal
          isOpen={showOverrideModal}
          onClose={handleOverrideCancel}
          onConfirm={handleOverrideConfirm}
          position={pendingLogin?.position}
        />
      </Container>
    </LoadingOverlay>
  );
};

export default SignInSelfService;
